import { Dictionary } from 'lodash';
import { useRef, useState } from 'react';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import styled from 'styled-components';
import Checkbox from '../../../elements/Checkbox';
import {
  selectContractorToolsLimit,
  selectGLAggregateLimit,
  selectGLLimit,
  selectLimitDamagePremRented,
  selectLocationBPPLimit,
  selectLocationBuildingLimit,
  selectCyberCoverage,
  selectWorkplaceViolenceCoverage,
  selectEPLICoverageLimit,
  selectEPLIRetentionLimit,
} from '../../../selectors/application';
import { CoverageInfo, endorsementsMap } from '../../AddOns/addOnQuestionMap';
import {
  Card,
  MainCont,
  MoreInfoCaretCont,
  MoreInfoCaret,
  StyledSmoothCollapse,
  StyledTableHeader,
  StyledTableBody,
  InfoBody,
  LimitHeader,
  LimitCell,
} from './styles';
import { useThemeContext } from '../../../elements/theme/CustomThemeProvider';

interface AddOnSummaryCardProps {
  endorsement: string;
  value: Dictionary<any> | string | number;
  glLimit: number;
  glAggregateLimit: number;
  bppLimit: number;
  buildingLimit: number;
  dispatch: Dispatch;
  limitDamagePremRented?: number;
  contractorToolsLimit?: number;
  cyberCoverage: { limits: string };
  workplaceViolenceCoverage: { limits: string };
  epliCoverageLimit: string;
  epliRetentionLimit: string;
}

const AddOnsSummaryCard = ({
  endorsement,
  value,
  dispatch,
  glLimit,
  glAggregateLimit,
  bppLimit,
  buildingLimit,
  limitDamagePremRented,
  contractorToolsLimit,
  cyberCoverage,
  workplaceViolenceCoverage,
  epliCoverageLimit,
  epliRetentionLimit,
}: AddOnSummaryCardProps) => {
  const { theme } = useThemeContext();
  const [isChecked, setIsChecked] = useState(true);
  const [isExpanded, setIsExpanded] = useState(false);
  const { LABEL, action, coverageInfo } = endorsementsMap[endorsement];

  // When a user toggles off contractor tools from the summary page, the limit gets reset when the bindable endpoint updates
  // this ref holds on to that limit value in case the users decides to toggle the add on back on
  const contractorToolsLimitRef = useRef(contractorToolsLimit);

  const handleOnCheck = () => {
    if (isChecked) {
      dispatch(action(null));
      setIsChecked(false);
      setIsExpanded(false);
    } else {
      setIsChecked(true);
      dispatch(action(value));
    }
  };

  return (
    <AddOnCard isChecked={isChecked}>
      <MainCont>
        <CheckboxCont>
          <Checkbox
            onChange={handleOnCheck}
            selected={isChecked}
            ariaLabel={`${isChecked ? 'remove' : 'add'} ${LABEL}`}
          />
        </CheckboxCont>

        <h3>{LABEL}</h3>
        <MoreInfoCaretCont isChecked={isChecked} onClick={() => isChecked && setIsExpanded(!isExpanded)}>
          {isChecked && (
            <MoreInfoCaret
              aria-label={`${isExpanded ? 'hide' : 'show'} ${LABEL} info`}
              src={theme.icons.caret}
              alt="More Info Caret Icon"
              expanded={isExpanded}
            />
          )}
        </MoreInfoCaretCont>
      </MainCont>
      <StyledSmoothCollapse expanded={isExpanded}>
        <StyledTableHeader>
          <thead>
            <tr>
              <th id={`${LABEL}-coverages`}>Coverage</th>
              <LimitHeader id={`${LABEL}-limits`}>Limit</LimitHeader>
            </tr>
          </thead>
        </StyledTableHeader>
        <InfoBody>
          <StyledTableBody>
            <tbody>
              {coverageInfo({
                glLimit,
                glAggregateLimit,
                bppLimit,
                buildingLimit,
                limitDamagePremRented: limitDamagePremRented as number,
                contractorToolsLimit: contractorToolsLimitRef.current as number,
                cyberLimits: cyberCoverage?.limits,
                workplaceViolenceCoverage: workplaceViolenceCoverage?.limits,
                epliCoverageLimit: epliCoverageLimit,
                epliRetentionLimit: epliRetentionLimit,
              }).map((info: CoverageInfo) => (
                <tr key={`${LABEL}-${info.coverage}`}>
                  <td headers={`${LABEL}-coverages`}>{info.coverage}</td>
                  <LimitCell headers={`${LABEL}-limits`}>{info.limit}</LimitCell>
                </tr>
              ))}
            </tbody>
          </StyledTableBody>
        </InfoBody>
      </StyledSmoothCollapse>
    </AddOnCard>
  );
};

const CheckboxCont = styled.div`
  align-self: center;
  margin-right: 16px;

  .checkbox-component-label {
    margin-bottom: 0;
  }
`;

const AddOnCard = styled(Card)`
  border: 1px solid #d4d4dd;
  @media screen and (max-width: 767px) {
    width: 100%;
    right: initial;
  }
`;

const mapStateToProps = (state: ReduxState) => ({
  glLimit: selectGLLimit(state) || 0,
  glAggregateLimit: selectGLAggregateLimit(state) || 0,
  bppLimit: selectLocationBPPLimit(state) || 0,
  buildingLimit: selectLocationBuildingLimit(state) || 0,
  limitDamagePremRented: selectLimitDamagePremRented(state),
  contractorToolsLimit: selectContractorToolsLimit(state),
  cyberCoverage: selectCyberCoverage(state),
  workplaceViolenceCoverage: selectWorkplaceViolenceCoverage(state),
  epliCoverageLimit: selectEPLICoverageLimit(state),
  epliRetentionLimit: selectEPLIRetentionLimit(state),
});

export default connect(mapStateToProps)(AddOnsSummaryCard);
